import '../ComponentNotificationConfig';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { GridNotification, IconBackground } from './styles/CardNotificationComponentStyles';
import { jobPreferencesVar } from '../../../cache/learner/jobPreferencesReactiveVar';
import { kanbanDataVar } from '../../../cache/learner/kanbanReactiveVar';
import AtomFlex from '../../../components/atoms/AtomFlex';
import AtomGrid from '../../../components/atoms/AtomGrid';
import AtomTypography from '../../../components/atoms/AtomTypography/AtomTypography';
import IconComponent from '../../../components/generics/IconComponent/IconComponent';
import { styleConfigs } from '../../../components/resources/styleConfigs';
import icons from '../../../constants/icons';
import useJobPreferencesDataHook from '../../../hooks/jobPreferencesDataHook/useJobPreferencesDataHook';
import { NotificationType } from '../../../operations/mutations/student/useMutationUpdateStudentSkillStatus/useMutationUpdateStudentSkillStatus';
import palette from '../../../theme/learnerPalette';

interface ICardNotificationComponent {
  notificationDetails: {
    id: number;
    type: NotificationType;
    skillName: string;
    occupationName: string;
    date: string;
    skillStatus: string;
  };
  beforeNotification?: boolean;
  isMobile?: boolean;
}

const CardNotificationComponent: React.FC<ICardNotificationComponent> = (props) => {
  const { beforeNotification, notificationDetails, isMobile } = props;
  const { t } = useTranslation('ComponentNotification');
  const { skillName, date, occupationName, type, skillStatus } = notificationDetails;
  const { operations } = useJobPreferencesDataHook(jobPreferencesVar, kanbanDataVar);

  const getNotificationIcon = (notificationType: NotificationType): ReactElement => {
    switch (notificationType) {
      case 'congratulations':
        return (
          <IconBackground>
            <IconComponent src={icons.PERFORMANCE_INCREASE_ICON} />
          </IconBackground>
        );

      case 'congratulations_no_job':
        return (
          <IconBackground>
            <IconComponent src={icons.PERFORMANCE_INCREASE_ICON} />
          </IconBackground>
        );

      default:
        return (
          <IconBackground>
            <IconComponent src={icons.PERFORMANCE_DOWN_ICON_NEW} sx={{ display: 'flex' }} />
          </IconBackground>
        );
    }
  };

  const getNotificationContent = (
    notificationType: NotificationType,
    skill: string,
    occupation: string,
    column: string,
  ): JSX.Element => {
    const occupationNumber = operations.getNumberOfOccupationsAffected(skill);
    let skillStatusFormatted = '';

    switch (column) {
      case 'completed':
        skillStatusFormatted = 'completed';
        break;

      case 'learning':
        skillStatusFormatted = 'learning';
        break;

      case 'to learn':
        skillStatusFormatted = 'backlog';
        break;

      default:
        skillStatusFormatted = column;
        break;
    }
    switch (notificationType) {
      case 'congratulations':
        return (
          <AtomTypography
            component="p"
            fontSize={styleConfigs.fonts.fontSize14}
            lineHeight={styleConfigs.fonts.lineHeight22}
            color={palette.light.text.mistBlue}
          >
            {occupationNumber > 0 ? (
              <>
                <AtomTypography
                  component="span"
                  fontWeight={700}
                  fontSize="inherit"
                  color={palette.light.text.prussianBlue}
                >
                  {`${skill} `}
                </AtomTypography>
                {occupationNumber > 1
                  ? t('NOTIFICATION_TEXT_NO_LONGER_PART_TEXT', {
                      column: t(skillStatusFormatted ?? ''),
                      occupations: occupationNumber.toString(),
                    })
                  : t('NOTIFICATION_TEXT_NO_COMPLETED_TEXT_SINGULAR', {
                      column: t(skillStatusFormatted ?? ''),
                      occupations: occupationNumber.toString(),
                    })}
              </>
            ) : (
              <>
                <AtomTypography
                  component="span"
                  fontWeight={700}
                  fontSize="inherit"
                  color={palette.light.text.prussianBlue}
                >
                  {`${skill} `}
                </AtomTypography>
                {t('NOTIFICATION_TEXT_ADDED_NO_OCCUPATION', { column: t(skillStatusFormatted ?? '') })}
              </>
            )}
          </AtomTypography>
        );

      case 'congratulations_no_job':
        return (
          <AtomTypography
            component="p"
            fontSize={styleConfigs.fonts.fontSize14}
            lineHeight={styleConfigs.fonts.lineHeight22}
            color={palette.light.text.mistBlue}
          >
            <AtomTypography
              component="span"
              fontWeight={700}
              fontSize="inherit"
              color={palette.light.text.prussianBlue}
            >
              {`${skill} `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_IS_COMPLETED_TEXT')}
          </AtomTypography>
        );

      case 'lookout':
        return (
          <AtomTypography
            component="p"
            fontSize={styleConfigs.fonts.fontSize14}
            lineHeight={styleConfigs.fonts.lineHeight22}
            color={palette.light.text.mistBlue}
          >
            <AtomTypography
              component="span"
              fontWeight={700}
              fontSize="inherit"
              color={palette.light.text.prussianBlue}
            >
              {`${skill} `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_IN_COLUMN_TEXT')}
            <AtomTypography component="span" fontSize="inherit" fontStyle={styleConfigs.fonts.italic}>
              {` ${t(skillStatusFormatted ?? '')}. `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_CHANCE_TEXT')}
            <AtomTypography component="span" fontSize="inherit" fontWeight={700}>
              {` ${operations.occupationsThatContainSkill({ skillName: skill })} `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_DECREASE_TEXT')}
          </AtomTypography>
        );

      case 'lookout_no_job':
        return (
          <AtomTypography
            component="p"
            fontSize={styleConfigs.fonts.fontSize14}
            lineHeight={styleConfigs.fonts.lineHeight22}
            color={palette.light.text.mistBlue}
          >
            <AtomTypography
              component="span"
              fontWeight={700}
              fontSize="inherit"
              color={palette.light.text.prussianBlue}
            >
              {`${skill} `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_IN_COLUMN_TEXT')}
            <AtomTypography component="span" fontSize="inherit" fontStyle={styleConfigs.fonts.italic}>
              {` ${t(skillStatusFormatted ?? '')}. `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_EXPLORE_TEXT')}
          </AtomTypography>
        );

      case 'lookout_removed':
        return (
          <AtomTypography
            component="p"
            fontSize={styleConfigs.fonts.fontSize14}
            lineHeight={styleConfigs.fonts.lineHeight22}
            color={palette.light.text.mistBlue}
          >
            {occupationNumber > 0 ? (
              <>
                <AtomTypography
                  component="span"
                  fontWeight={700}
                  fontSize="inherit"
                  color={palette.light.text.prussianBlue}
                >
                  {`${skill} `}
                </AtomTypography>
                {occupationNumber > 1
                  ? t('NOTIFICATION_TEXT_NO_LONGER_PART_TEXT', {
                      column: t(skillStatusFormatted ?? ''),
                      occupations: occupationNumber.toString(),
                    })
                  : t('NOTIFICATION_TEXT_NO_LONGER_PART_TEXT_SINGULAR', {
                      column: t(skillStatusFormatted ?? ''),
                      occupations: occupationNumber.toString(),
                    })}
              </>
            ) : (
              <>
                <AtomTypography
                  component="span"
                  fontWeight={700}
                  fontSize="inherit"
                  color={palette.light.text.prussianBlue}
                >
                  {`${skill} `}
                </AtomTypography>
                {t('NOTIFICATION_TEXT_NO_LONGER_PART_NO_JOB_TEXT')}
              </>
            )}
          </AtomTypography>
        );

      case 'lookout_removed_no_job':
        return (
          <AtomTypography
            component="p"
            fontSize={styleConfigs.fonts.fontSize14}
            lineHeight={styleConfigs.fonts.lineHeight22}
            color={palette.light.text.mistBlue}
          >
            <AtomTypography
              component="span"
              fontWeight={700}
              fontSize="inherit"
              color={palette.light.text.prussianBlue}
            >
              {`${skill} `}
            </AtomTypography>
            {t('NOTIFICATION_TEXT_NO_LONGER_PART_NO_JOB_TEXT')}
          </AtomTypography>
        );

      default:
        return <></>;
    }
  };

  return (
    <GridNotification
      container
      flexWrap={isMobile ? 'nowrap' : 'wrap'}
      backgroundcolor={!beforeNotification ? palette.light.background.cadetGrey : ''}
      py={1}
      px={isMobile ? 1 : 0}
      gap={isMobile ? 1.5 : 0}
    >
      <AtomGrid item sm={2}>
        <AtomFlex direction="column" alignItems="center" justifyContent="flex-start">
          {getNotificationIcon(type)}
        </AtomFlex>
      </AtomGrid>
      <AtomGrid item sm={10} justifyContent="center" alignItems="flex-start">
        {getNotificationContent(type, skillName, occupationName, skillStatus)}
        <AtomFlex direction="row" spacing={0.5}>
          <WatchLaterIcon fontSize="small" color="disabled" />
          <AtomTypography variant="body2" color={palette.light.text.amethyst}>
            {date}
          </AtomTypography>
        </AtomFlex>
      </AtomGrid>
    </GridNotification>
  );
};

export default CardNotificationComponent;
